// Distribution plot component with channel values and z-scores
export const DistributionPlot = ({ model, channelValues, channelValuesLoading, zScores, zScoresLoading, showZScores = true }) => {
    // For z-scores, use a range of -3 to +3 standard deviations
    const zScoreRange = 3;
    // For raw values, use a range of mean ± 3*std
    const displayStdDevMultiple = 3;

    const formatLabelForCard = (value) => {
        const absValue = Math.abs(value);
        if (absValue >= 1000) {
            return value.toFixed(0);
        } else if (absValue >= 100) {
            return value.toFixed(1);
        } else {
            return value.toFixed(2);
        }
    };

    // For z-scores, normalize between -3 and +3
    const normalizeZScore = (zScoreInput) => {
        // Clamp the zScoreInput to the range [-zScoreRange, zScoreRange]
        const clampedZScore = Math.max(-zScoreRange, Math.min(zScoreRange, zScoreInput));
        // Convert clamped z-score to percentage position (0-100%)
        // -3 should be at 0%, 0 at 50%, and +3 at 100%
        return ((clampedZScore + zScoreRange) / (2 * zScoreRange)) * 100;
    };

    // For raw values, normalize relative to mean ± 3*std
    const normalizeRelativeToCenteredMean = (value) => {
        if (model.std === 0) {
            return 50;
        }
        const plotDisplayMin = model.mean - (displayStdDevMultiple * model.std);
        const plotDisplayMax = model.mean + (displayStdDevMultiple * model.std);
        const plotDisplayRange = plotDisplayMax - plotDisplayMin;
        if (plotDisplayRange === 0) {
            return 50;
        }
        const normalizedValue = ((value - plotDisplayMin) / plotDisplayRange) * 100;
        return Math.max(0, Math.min(100, normalizedValue));
    };

    // Fixed positions for standard z-score markers
    const zeroPos = 50; // 0 is always at 50%
    const minusOnePos = normalizeZScore(-1);
    const plusOnePos = normalizeZScore(1);
    const minusTwoPos = normalizeZScore(-2);
    const plusTwoPos = normalizeZScore(2);

    // Fixed positions for raw value markers
    const meanPos = 50; // Mean is always at 50%
    const meanMinusStdPos = normalizeRelativeToCenteredMean(model.mean - model.std);
    const meanPlusStdPos = normalizeRelativeToCenteredMean(model.mean + model.std);
    const meanMinus2StdPos = normalizeRelativeToCenteredMean(model.mean - 2 * model.std);
    const meanPlus2StdPos = normalizeRelativeToCenteredMean(model.mean + 2 * model.std);

    return (
        <div className="normative-plot">
            <div className="distribution-plot-area">
                <div
                    className="distribution-gaussian-curve"
                    style={{
                        height: '80px',
                        background: 'linear-gradient(to right, transparent, rgba(100, 140, 255, 0.5), transparent)',
                        borderRadius: '50%/100%',
                        position: 'relative'
                    }}
                >
                    {/* Center line - either Z-score = 0 or Mean */}
                    <div
                        className="distribution-mean-line"
                        style={{
                            position: 'absolute',
                            left: showZScores ? `${zeroPos}%` : `${meanPos}%`,
                            top: '0',
                            height: '100%',
                            width: '3px', // Thickest for mean
                            backgroundColor: 'rgba(255, 255, 255, 0.8)'
                        }}
                    ></div>

                    {/* Standard deviation lines */}
                    {/* ±1 std */}
                    <div
                        className="distribution-std-minus"
                        style={{
                            position: 'absolute',
                            left: showZScores ? `${minusOnePos}%` : `${meanMinusStdPos}%`,
                            top: '0',
                            height: '100%',
                            width: '2px',
                            backgroundColor: 'rgba(255, 255, 255, 0.5)'
                        }}
                    ></div>
                    <div
                        className="distribution-std-plus"
                        style={{
                            position: 'absolute',
                            left: showZScores ? `${plusOnePos}%` : `${meanPlusStdPos}%`,
                            top: '0',
                            height: '100%',
                            width: '2px',
                            backgroundColor: 'rgba(255, 255, 255, 0.5)'
                        }}
                    ></div>

                    {/* ±2 std */}
                    <div
                        className="distribution-std-minus-2"
                        style={{
                            position: 'absolute',
                            left: showZScores ? `${minusTwoPos}%` : `${meanMinus2StdPos}%`,
                            top: '0',
                            height: '100%',
                            width: '1px',
                            backgroundColor: 'rgba(255, 255, 255, 0.4)'
                        }}
                    ></div>
                    <div
                        className="distribution-std-plus-2"
                        style={{
                            position: 'absolute',
                            left: showZScores ? `${plusTwoPos}%` : `${meanPlus2StdPos}%`,
                            top: '0',
                            height: '100%',
                            width: '1px',
                            backgroundColor: 'rgba(255, 255, 255, 0.4)'
                        }}
                    ></div>

                    {/* Plot individual channel z-scores as dots */}
                    {showZScores && !zScoresLoading && zScores && zScores.map((score, index) => {
                        const position = normalizeZScore(score.z_score);
                        const currentZScore = Math.abs(score.z_score); // Use absolute value for coloring
                        const colorIntensity = Math.min(1, currentZScore / 3); // Cap at 3 standard deviations
                        const dotColor = `rgba(${255}, ${220 - 160 * colorIntensity}, ${0}, 0.8)`;

                        return (
                            <div
                                key={`${score.patient_id}-${score.channel}-${index}`}
                                className="channel-value-dot"
                                style={{
                                    position: 'absolute',
                                    left: `${position}%`,
                                    top: '50%', // Center all points vertically
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: dotColor,
                                    zIndex: 2,
                                    transform: 'translate(-50%, -50%)',
                                    boxShadow: '0 0 4px rgba(0, 0, 0, 0.3)',
                                    cursor: 'pointer',
                                    animation: 'fadeIn 0.2s ease-in-out',
                                    opacity: 1
                                }}
                            />
                        );
                    })}

                    {/* If showing raw values, plot them directly */}
                    {!showZScores && !channelValuesLoading && channelValues && channelValues.map((channel, index) => {
                        const position = normalizeRelativeToCenteredMean(channel.value);
                        const zScoreForColor = model.std > 0 ? Math.abs((channel.value - model.mean) / model.std) : 0;
                        const colorIntensity = Math.min(1, zScoreForColor / 3); // Cap at 3 standard deviations
                        const dotColor = `rgba(${255}, ${220 - 160 * colorIntensity}, ${0}, 0.8)`;

                        return (
                            <div
                                key={`${channel.patient_id}-${channel.channel}-${index}`}
                                className="channel-value-dot"
                                style={{
                                    position: 'absolute',
                                    left: `${position}%`,
                                    top: '50%', // Center all points vertically
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: dotColor,
                                    zIndex: 2,
                                    transform: 'translate(-50%, -50%)',
                                    boxShadow: '0 0 4px rgba(0, 0, 0, 0.3)',
                                    cursor: 'pointer',
                                    animation: 'fadeIn 0.2s ease-in-out',
                                    opacity: 1
                                }}
                            />
                        );
                    })}
                </div>
                {/* X-axis ticks showing appropriate scale */}
                <div className="distribution-axis" style={{ position: 'relative', height: '20px', marginTop: '4px' }}>
                    {showZScores ? (
                        // Z-score scale
                        <>
                            <span style={{ position: 'absolute', left: `${minusTwoPos}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>-2</span>
                            <span style={{ position: 'absolute', left: `${minusOnePos}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>-1</span>
                            <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>0</span>
                            <span style={{ position: 'absolute', left: `${plusOnePos}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>+1</span>
                            <span style={{ position: 'absolute', left: `${plusTwoPos}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>+2</span>
                        </>
                    ) : (
                        // Raw value scale
                        <>
                            <span style={{ position: 'absolute', left: `${meanMinus2StdPos}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>{formatLabelForCard(model.mean - 2 * model.std)}</span>
                            <span style={{ position: 'absolute', left: `${meanMinusStdPos}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>{formatLabelForCard(model.mean - model.std)}</span>
                            <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>{formatLabelForCard(model.mean)}</span>
                            <span style={{ position: 'absolute', left: `${meanPlusStdPos}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>{formatLabelForCard(model.mean + model.std)}</span>
                            <span style={{ position: 'absolute', left: `${meanPlus2StdPos}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>{formatLabelForCard(model.mean + 2 * model.std)}</span>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
